/**
 * Canonical File-Level Donor Metrics Contract
 *
 * Single source of truth for PORTFOLIO (file-level) donor metrics:
 * cohort retention, replacement ratio, and the Donor Health Index.
 * Pure + deterministic; `today` is injectable.
 *
 * Two different questions, two clearly-named outputs:
 *  - Cohort question   -> computeFileMetricsByPeriod (fiscal-year cohorts)
 *  - Standing question -> computeFileStanding (point-in-time RFM rollup)
 *
 * No other module may re-implement cohort/replacement formulas.
 * @see /docs/superpowers/specs/2026-06-03-canonical-file-metrics-dhi-replacement-ratio-design.md
 */

import { isLapsed, isAtRisk } from "./donorMetrics";

// ── Period model ────────────────────────────────────────────────────────────

/** Throws if the fiscal start month is not an integer 1–12. */
export function assertValidFiscalStartMonth(month: number): void {
  if (
    typeof month !== "number" ||
    !Number.isInteger(month) ||
    month < 1 ||
    month > 12
  ) {
    throw new Error(
      `fileMetrics: fiscalYearStartMonth must be an integer 1-12, got ${String(month)}`,
    );
  }
}

/**
 * Map a gift date to its fiscal-year integer.
 * The fiscal year is labeled by the calendar year in which it ENDS.
 * fiscalYearStartMonth = 1 reproduces calendar-year behavior exactly.
 */
export function assignPeriod(
  date: string,
  fiscalYearStartMonth: number,
): number {
  assertValidFiscalStartMonth(fiscalYearStartMonth);
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`assignPeriod: invalid date "${date}"`);
  }
  const month = d.getUTCMonth() + 1; // 1-12
  const year = d.getUTCFullYear();
  if (fiscalYearStartMonth === 1) return year;
  return month >= fiscalYearStartMonth ? year + 1 : year;
}

/** Last calendar date of the given fiscal period. */
function periodEndDate(period: number, fiscalYearStartMonth: number): Date {
  // End month (1-based) is the month before the start month; year is the label year.
  const endMonth = fiscalYearStartMonth === 1 ? 12 : fiscalYearStartMonth - 1;
  // Date.UTC(year, monthIndex=endMonth, day=0) => last day of 1-based month `endMonth`.
  return new Date(Date.UTC(period, endMonth, 0));
}

/** True when the fiscal period has fully elapsed as of `today`. */
export function isPeriodComplete(
  period: number,
  fiscalYearStartMonth: number,
  today: Date = new Date(),
): boolean {
  assertValidFiscalStartMonth(fiscalYearStartMonth);
  return (
    periodEndDate(period, fiscalYearStartMonth).getTime() <= today.getTime()
  );
}

/** Human label for a fiscal period (e.g. "FY2025"). */
export function formatPeriodLabel(
  period: number,
  _fiscalYearStartMonth: number,
): string {
  return `FY${period}`;
}

// ── Cohort metrics ──────────────────────────────────────────────────────────

export interface FileGift {
  donorId: string;
  amountCents: number;
  date: string; // ISO
}

export interface PeriodConfig {
  fiscalYearStartMonth: number; // 1–12
}

export interface FilePeriodMetrics {
  period: number;
  label: string;
  complete: boolean;
  activeDonors: number;
  retainedDonors: number;
  lostDonors: number;
  acquiredDonors: number;
  reactivatedDonors: number;
  totalRaisedCents: number;
  retentionRate: number | null; // null when no prior period
  revenueRetentionRate: number | null;
  replacementRatio: number | null; // null when lostDonors === 0
  grossReplacementRatio: number | null;
}

function countActiveInPeriod(
  donorPeriods: Map<string, Set<number>>,
  period: number,
): number {
  let n = 0;
  for (const set of donorPeriods.values()) if (set.has(period)) n++;
  return n;
}

/**
 * Canonical cohort metrics, one entry per requested period.
 *
 * Definitions (period P vs prior P-1), a donor "active in P" = gave >=1 gift in P:
 *  - retained(P)    = active in P and P-1
 *  - lost(P)        = active in P-1 and NOT in P
 *  - acquired(P)    = active in P and first-ever gift falls in P (true new)
 *  - reactivated(P) = active in P, not in P-1, but gave before P-1
 *  - replacementRatio = acquired / lost (headline, true-new only)
 */
export function computeFileMetricsByPeriod(
  gifts: FileGift[],
  periods: number[],
  config: PeriodConfig,
  today: Date = new Date(),
): Record<number, FilePeriodMetrics> {
  assertValidFiscalStartMonth(config.fiscalYearStartMonth);
  const m = config.fiscalYearStartMonth;

  const donorPeriods = new Map<string, Set<number>>();
  const donorFirstPeriod = new Map<string, number>();
  const periodRevenue = new Map<number, number>();
  const donorPeriodRevenue = new Map<string, Map<number, number>>();

  for (const gift of gifts) {
    const p = assignPeriod(gift.date, m);

    let set = donorPeriods.get(gift.donorId);
    if (!set) {
      set = new Set();
      donorPeriods.set(gift.donorId, set);
    }
    set.add(p);

    const first = donorFirstPeriod.get(gift.donorId);
    if (first === undefined || p < first) donorFirstPeriod.set(gift.donorId, p);

    periodRevenue.set(p, (periodRevenue.get(p) ?? 0) + gift.amountCents);

    let dr = donorPeriodRevenue.get(gift.donorId);
    if (!dr) {
      dr = new Map();
      donorPeriodRevenue.set(gift.donorId, dr);
    }
    dr.set(p, (dr.get(p) ?? 0) + gift.amountCents);
  }

  const result: Record<number, FilePeriodMetrics> = {};

  for (const P of periods) {
    const prev = P - 1;
    let active = 0,
      retained = 0,
      lost = 0,
      acquired = 0,
      reactivated = 0;
    let retainedRevenue = 0;

    for (const [donorId, set] of donorPeriods) {
      const inP = set.has(P);
      const inPrev = set.has(prev);
      if (inP) {
        active++;
        const first = donorFirstPeriod.get(donorId)!;
        if (inPrev) {
          retained++;
          retainedRevenue += donorPeriodRevenue.get(donorId)?.get(P) ?? 0;
        } else if (first === P) {
          acquired++;
        } else if (first < prev) {
          reactivated++;
        }
      } else if (inPrev) {
        lost++;
      }
    }

    const activePrev = countActiveInPeriod(donorPeriods, prev);
    const priorRevenue = periodRevenue.get(prev) ?? 0;

    result[P] = {
      period: P,
      label: formatPeriodLabel(P, m),
      complete: isPeriodComplete(P, m, today),
      activeDonors: active,
      retainedDonors: retained,
      lostDonors: lost,
      acquiredDonors: acquired,
      reactivatedDonors: reactivated,
      totalRaisedCents: periodRevenue.get(P) ?? 0,
      retentionRate: activePrev > 0 ? retained / activePrev : null,
      revenueRetentionRate:
        priorRevenue > 0 ? retainedRevenue / priorRevenue : null,
      replacementRatio: lost > 0 ? acquired / lost : null,
      grossReplacementRatio: lost > 0 ? (acquired + reactivated) / lost : null,
    };
  }

  return result;
}

// ── Current standing (the OTHER question) ───────────────────────────────────

export interface StandingDonorInput {
  daysSinceLastGift?: number;
  isMonthlyDonor?: boolean;
}

export interface FileStanding {
  asOf: string;
  activeCount: number;
  atRiskCount: number;
  lapsedCount: number;
  monthlyCount: number;
  /** (active + atRisk) / total — the RFM standing proxy. NEVER call this retentionRate. */
  currentEngagementRate: number;
}

/**
 * Point-in-time rollup composed from the canonical per-donor recency flags
 * (`isLapsed` / `isAtRisk` from donorMetrics.ts). Answers "how engaged is the
 * file right now" — a DIFFERENT question from cohort retention.
 */
export function computeFileStanding(
  donors: StandingDonorInput[],
  today: Date = new Date(),
): FileStanding {
  let active = 0,
    atRisk = 0,
    lapsed = 0,
    monthly = 0;
  for (const d of donors) {
    if (isLapsed(d.daysSinceLastGift)) lapsed++;
    else if (isAtRisk(d.daysSinceLastGift)) atRisk++;
    else if (typeof d.daysSinceLastGift === "number") active++;
    if (d.isMonthlyDonor) monthly++;
  }
  const total = donors.length;
  return {
    asOf: today.toISOString().slice(0, 10),
    activeCount: active,
    atRiskCount: atRisk,
    lapsedCount: lapsed,
    monthlyCount: monthly,
    currentEngagementRate: total > 0 ? (active + atRisk) / total : 0,
  };
}

// ── Donor Health Index (composition only) ───────────────────────────────────

export type DhiCategory =
  | "excellent"
  | "healthy"
  | "watch"
  | "at_risk"
  | "critical";

export interface DhiComponent {
  key: string;
  label: string;
  value: number; // normalized 0–1
  weight: number;
  contribution: number; // weight * value * 100
}

export type DonorHealthIndex =
  | {
      status: "scored";
      score: number;
      category: DhiCategory;
      basisPeriod: number;
      components: DhiComponent[];
    }
  | {
      status: "insufficient_history";
      score: null;
      category: null;
      basisPeriod: null;
      components: [];
    };

/** Tunable weights — must sum to 1. */
export const DHI_WEIGHTS = {
  retentionRate: 0.3,
  replacementRatio: 0.2,
  retentionTrend: 0.15,
  acquisitionTrend: 0.15,
  reactivation: 0.1,
  monthlyShare: 0.1,
} as const;

/** Tunable normalization + band constants. */
export const DHI_REPLACEMENT_RATIO_TARGET = 1.2;
export const DHI_RETENTION_TREND_SENSITIVITY = 5;
export const DHI_REACTIVATION_SCALE = 5;
export const DHI_MONTHLY_SHARE_SCALE = 3;
export const DHI_BANDS: ReadonlyArray<{ min: number; category: DhiCategory }> =
  [
    { min: 80, category: "excellent" },
    { min: 65, category: "healthy" },
    { min: 50, category: "watch" },
    { min: 35, category: "at_risk" },
    { min: -Infinity, category: "critical" },
  ];

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));

export function categorizeDhi(score: number): DhiCategory {
  return DHI_BANDS.find((b) => score >= b.min)!.category;
}

function buildComponents(
  basis: FilePeriodMetrics,
  prior: FilePeriodMetrics | undefined,
  standing: FileStanding,
): DhiComponent[] {
  const retentionRate =
    basis.retentionRate == null ? 0.5 : clamp01(basis.retentionRate);

  const replacementRatio =
    basis.replacementRatio == null
      ? 0.5
      : clamp01(basis.replacementRatio / DHI_REPLACEMENT_RATIO_TARGET);

  const retentionTrend =
    basis.retentionRate == null || !prior || prior.retentionRate == null
      ? 0.5
      : clamp01(
          0.5 +
            (basis.retentionRate - prior.retentionRate) *
              DHI_RETENTION_TREND_SENSITIVITY,
        );

  const acquisitionTrend = !prior
    ? 0.5
    : clamp01(
        0.5 +
          ((basis.acquiredDonors - prior.acquiredDonors) /
            Math.max(prior.acquiredDonors, 1)) *
            0.5,
      );

  const reactivation = clamp01(
    (basis.reactivatedDonors / Math.max(basis.activeDonors, 1)) *
      DHI_REACTIVATION_SCALE,
  );

  const standingTotal =
    standing.activeCount + standing.atRiskCount + standing.lapsedCount;
  const monthlyShare = clamp01(
    (standingTotal > 0 ? standing.monthlyCount / standingTotal : 0) *
      DHI_MONTHLY_SHARE_SCALE,
  );

  const raw: Array<{
    key: keyof typeof DHI_WEIGHTS;
    label: string;
    value: number;
  }> = [
    { key: "retentionRate", label: "Retention rate", value: retentionRate },
    {
      key: "replacementRatio",
      label: "Replacement ratio",
      value: replacementRatio,
    },
    { key: "retentionTrend", label: "Retention trend", value: retentionTrend },
    {
      key: "acquisitionTrend",
      label: "Acquisition trend",
      value: acquisitionTrend,
    },
    {
      key: "reactivation",
      label: "Reactivation contribution",
      value: reactivation,
    },
    {
      key: "monthlyShare",
      label: "Monthly/recurring share",
      value: monthlyShare,
    },
  ];

  return raw.map((r) => {
    const weight = DHI_WEIGHTS[r.key];
    return {
      key: r.key,
      label: r.label,
      value: r.value,
      weight,
      contribution: weight * r.value * 100,
    };
  });
}

/**
 * Composition-only health score. Reads exclusively from the canonical metrics
 * above — computes no donor/portfolio math of its own. Scores the most-recent
 * COMPLETE fiscal period unless `opts.includeIncompletePeriod` is set.
 */
export function computeDonorHealthIndex(
  series: FilePeriodMetrics[],
  standing: FileStanding,
  opts: { includeIncompletePeriod?: boolean } = {},
): DonorHealthIndex {
  const sorted = [...series].sort((a, b) => a.period - b.period);
  const eligible = opts.includeIncompletePeriod
    ? sorted
    : sorted.filter((p) => p.complete);

  if (eligible.length === 0) {
    return {
      status: "insufficient_history",
      score: null,
      category: null,
      basisPeriod: null,
      components: [],
    };
  }

  const basis = eligible[eligible.length - 1];
  const prior = eligible[eligible.length - 2];
  const components = buildComponents(basis, prior, standing);
  const raw = components.reduce((s, c) => s + c.contribution, 0);
  const score = Math.round(Math.max(0, Math.min(100, raw)));

  return {
    status: "scored",
    score,
    category: categorizeDhi(score),
    basisPeriod: basis.period,
    components,
  };
}
